import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Button
          onClick={() => navigate("/")}
          variant="ghost"
          className="mb-8"
        >
          ← Back to Home
        </Button>

        <h1 className="text-4xl font-bold mb-4 font-serif">Privacy Policy</h1>
        <p className="text-sm text-gray-600 mb-8">Last Updated: October 21, 2025</p>

        <div className="prose prose-lg max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold mb-3 font-serif">Introduction</h2>
            <p>
              Welcome to the 21-Day AI Challenge ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you subscribe to our email campaign.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3 font-serif">Information We Collect</h2>
            <p>We collect the following information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Email Address:</strong> Provided when you sign up for the campaign</li>
              <li><strong>Subscription Data:</strong> Start date, subscription status, confirmation status</li>
              <li><strong>Email Engagement Data:</strong> Email opens, clicks, bounces, and delivery status (collected via Postmark)</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information (automatically collected)</li>
              <li><strong>Error and Performance Data:</strong> Application errors, performance metrics, and user interactions to improve service reliability (collected via Sentry)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3 font-serif">How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Deliver daily educational emails as part of the 21-Day AI Challenge</li>
              <li>Track email engagement to improve content quality</li>
              <li>Manage your subscription preferences</li>
              <li>Send confirmation emails when you sign up</li>
              <li>Monitor application performance and identify technical issues</li>
              <li>Improve service reliability and user experience</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3 font-serif">How We Store Your Data</h2>
            <p>
              Your data is stored securely using Supabase, a trusted cloud database platform. We implement appropriate security measures to protect your personal information from unauthorized access, alteration, or deletion.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3 font-serif">Third-Party Services</h2>
            <p>We use the following third-party services:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Postmark:</strong> For sending and tracking emails</li>
              <li><strong>Supabase:</strong> For data storage and management</li>
              <li><strong>Sentry:</strong> For error monitoring, performance tracking, and session replay to help us identify and fix technical issues</li>
            </ul>
            <p className="mt-2">
              These services may collect and process data according to their own privacy policies. We carefully select partners who maintain high data protection standards.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3 font-serif">Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Unsubscribe:</strong> Click the unsubscribe link in any email to stop receiving communications</li>
              <li><strong>Access Your Data:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Delete Your Data:</strong> Request deletion of your personal information</li>
              <li><strong>Correct Your Data:</strong> Update or correct your information</li>
              <li><strong>Object to Processing:</strong> Object to how we process your data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3 font-serif">Data Retention</h2>
            <p>
              We retain your email address and subscription data for as long as you remain subscribed to the campaign. After you unsubscribe, we retain your email on a suppression list to ensure we don't accidentally re-add you to our mailing list. You may request full deletion of your data at any time by contacting us at privacy@avodah.dev.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3 font-serif">International Data Transfers</h2>
            <p>
              Your data may be transferred to and stored on servers located outside your country. We ensure appropriate safeguards are in place to protect your data in accordance with applicable data protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3 font-serif">Children's Privacy</h2>
            <p>
              Our service is not directed to individuals under the age of 13. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3 font-serif">Changes to This Policy</h2>
            <p>
              We may update this privacy policy from time to time. We will notify you of any significant changes by posting the new policy on this page and updating the "Last Updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3 font-serif">Contact Us</h2>
            <p>
              If you have questions about this privacy policy or wish to exercise your data rights, please contact us at:
            </p>
            <p className="mt-2">
              Email: <a href="mailto:privacy@avodah.dev" className="text-blue-600 hover:underline">privacy@avodah.dev</a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t">
          <Button onClick={() => navigate("/")} className="w-full sm:w-auto">
            Return to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
